import React, { useContext, useMemo } from 'react';
import { ButtonV2, useDisclosure, TextV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationCombined } from '../Accommodations/Accommodation.types';
import { Category } from '@graphql/generated';
import { AnalyticsContext, Link, useTranslation } from '@shared/core';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { button2ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useEventDisplayName, useEventInfo } from '@shared/utils/eventInfo';
import { EventVenue } from '@shared/utils/eventPlace';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonStylesWithAccentColor } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { createSearchParamsFromAccommodationsPage, useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import AccommodationsConsoleDialog from '@apps/accommodations';
import { AccommodationsConsoleConfigProvider } from '@apps/accommodations/AccommodationsConsoleConfigContext';

interface ExploreOtherOptionsProps {
  pageSlug: string;
  eventTimezone: Maybe<string>;
  venue: Maybe<EventVenue>;
  accommodations: Maybe<AccommodationCombined[]>;
}

export const ExploreOtherOptions: React.FC<ExploreOtherOptionsProps> = ({ pageSlug, eventTimezone: timezone, venue, accommodations }) => {
  const analytics = useContext(AnalyticsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isTravelAccommodationsConsoleV2Enabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');

  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const { accentColor } = useColorPaletteProvider();
  const buttonStyles = useMemo(() => (accentColor ? getButtonStylesWithAccentColor(accentColor) : {}), [accentColor]);

  const { t2 } = useTranslation('guestSiteCustom');
  const bookingAssistantTrans = t2('bookingAssistant');

  const hotelRoute = useHotelRoutePaths();

  const { eventHandle } = useEventInfo();
  const eventDisplayName = useEventDisplayName();
  const url = hotelRoute.hotelSearch.buildRelativeUrl(`${eventHandle}`, createSearchParamsFromAccommodationsPage('explore_other_options'));

  const handleOnClick = useEventCallback(() => {
    if (isTravelAccommodationsConsoleV2Enabled) {
      onOpen();
      return;
    }

    analytics.track({
      action: 'LinkInteracted',
      category: 'linkify',
      label: 'accommodations',
      extraInfo: {
        url,
        actionType: 'click',
        source: 'accommodations',
      },
    });
    analytics.track({
      action: 'BookingAssistantClicked',
      actionType: 'click',
      category: 'wedding',
      pagePrefix: 'guest',
      page: pageSlug,
    });
  });

  return (
    <>
      <GuestSiteTypographyOverride
        override={button2ToButtonsOverride}
        defaultFontFamily="Inter UI"
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={6}
          paddingX="24px"
        >
          <TextV2
            typographyVariant="body3"
            color={accentColor}
            fontSize="14px"
          >
            {bookingAssistantTrans.title}
          </TextV2>
          <ButtonV2
            width={pxToRem(244)}
            typographyVariant="button2"
            intent="neutral"
            shape="rounded"
            variant="outline"
            fontWeight={600}
            fontSize={scaleFontSize(pxToRem(16), Category.BUTTONS)}
            {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
            onClick={handleOnClick}
            {...(!isTravelAccommodationsConsoleV2Enabled && { as: Link, to: url })}
            {...buttonStyles}
          >
            {bookingAssistantTrans.cta}
          </ButtonV2>
        </Flex>
      </GuestSiteTypographyOverride>
      <AccommodationsConsoleConfigProvider>
        <AccommodationsConsoleDialog
          isOpen={isOpen}
          onClose={onClose}
          eventHandle={eventHandle}
          eventName={eventDisplayName}
        />
      </AccommodationsConsoleConfigProvider>
    </>
  );
};
